import { featureFlag } from 'src/environments/environment';

/**
 * The fields an external (non-user) approver must supply.
 *
 * Deliberately a whitelist. The backend sends the whole person object back,
 * including fields the form has no input for. `name` is one: it is a leftover
 * from the single "name-surname" field that 187b42ec2 split into first_name /
 * last_name, and for a member saved with `no_person` the backend returns it as
 * null forever (`loa_member_retrieve_person_data` in core). A blacklist of
 * "optional" keys treats every such field as required, which is unfillable and
 * blocks Save. Listing what is genuinely required keeps that from happening
 * again when the backend adds a field.
 *
 * `job_position` is not here on purpose: `Person.job_position` is
 * `blank=True, null=True`, the backend never validates it, and the contract
 * flow hides the input entirely.
 */
export const REQUIRED_NON_USER_PERSON_FIELDS = [
  'email',
  'first_name',
  'last_name',
];

export function emailIsValid(email: any): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function phoneIsValid(phoneNumber: any): boolean {
  const pattern = /^(\d{3}-\d{3}-\d{4}|\d{10})$/;
  return phoneNumber != null && pattern.test(phoneNumber);
}

/** True when `person` is an external approver's detail object.
 *
 * An internal approver's `person` is the person's id, so it must never be run
 * through the non-user field checks.
 */
function isNonUserPerson(person: any): boolean {
  return (
    !!person &&
    typeof person === 'object' &&
    !Number.isInteger(person)
  );
}

/** True when a required field of an external approver is missing. */
export function hasEmptyProperty(person: any): boolean {
  if (!isNonUserPerson(person)) {
    return false;
  }
  return REQUIRED_NON_USER_PERSON_FIELDS.some(
    (field) =>
      person[field] === null ||
      person[field] === undefined ||
      person[field] === '',
  );
}

/** True when an external approver slot has been left entirely untouched. */
function isUntouchedNonUser(member: any): boolean {
  const person = member.person;
  if (!isNonUserPerson(person)) {
    return false;
  }
  return (
    !person.email &&
    !person.first_name &&
    !person.last_name &&
    !person.job_position &&
    !person.phone_number
  );
}

export function checkMail(levels: any): boolean {
  return !levels?.some((level: any) => {
    if (level.user_type === 'user') {
      return false;
    }
    return level.members?.some((member: any) => {
      // A `no_person` slot nobody has filled in yet is not an email error.
      if (member.no_person && isUntouchedNonUser(member)) {
        return false;
      }
      return (
        isNonUserPerson(member.person) &&
        !emailIsValid(member.person?.email)
      );
    });
  });
}

export function checkPhoneNumber(levels: any): boolean {
  if (!featureFlag.ddoc) {
    return true;
  }
  return !levels?.some((level: any) => {
    if (level.user_type === 'user') {
      return false;
    }
    return level.members?.some((member: any) => {
      // The phone number is only required of ddoc signers (section 26/28);
      // for anyone else an empty one is fine.
      if (
        !member.ddoc_use_26 &&
        !member.ddoc_use_28 &&
        (member.person?.phone_number === '' ||
          member.person?.phone_number === null ||
          member.person?.phone_number === undefined)
      ) {
        return false;
      }
      return (
        isNonUserPerson(member.person) &&
        !phoneIsValid(member.person?.phone_number)
      );
    });
  });
}

export function checkEmptyApprover(
  levels: any,
  allowNoPerson = false,
  excludeLvlFn?: (level: any) => boolean,
) {
  const someEmpty = levels?.some((level: any) => {
    if (excludeLvlFn && excludeLvlFn(level)) {
      return false;
    }
    let members = level.members;
    if (allowNoPerson) {
      members = members.filter((member: any) => !member.no_person);
    }

    return members.some(
      (member: any) =>
        !member.person || hasEmptyProperty(member.person),
    );
  });
  if (someEmpty) {
    return false;
  }
  return true;
}
